import { swSettings } from "app/common/services/swSettings";
import {
    INDUSTRY_SEGMENTATION_CONFIG_ID,
    MARKET_PLAYERS_CONFIG_ID,
    MARKET_RESEARCH_HOME_CONFIG_ID,
    MARKET_RESEARCH_PERFORMANCE_CONFIG_ID,
} from "app/pages/industry-analysis/config/const";
import {
    APP_CATEGORY_LEADERBOARD,
    APP_CATEGORY_TRENDS,
    WEBSITE_ANALYSIS_MARKETING_CHANNELS,
    WEBSITE_ANALYSIS_MARKETING_DISPLAY_AD_NETWORKS,
    WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW,
    WEBSITE_ANALYSIS_MARKETING_KEYWORDS,
    WEBSITE_ANALYSIS_MARKETING_SEARCH_ADS,
    WEBSITE_ANALYSIS_MARKETING_SEARCH_COMPETITORS,
    WEBSITE_ANALYSIS_MARKETING_SEARCH_OVERVIEW,
    WEBSITE_ANALYSIS_MARKETING_SOCIAL_OVERVIEW,
} from "app/pages/sales-intelligence/constants/routes";
import {
    DIGITALSUITE_WEBSITE_AUDIENCE,
    DIGITALSUITE_WEBSITE_PUBLISHER_OVERVIEW,
    DIGITALSUITE_WEBSITE_REFERRALS,
} from "app/pages/website-analysis/constants/constants";
import { EHooksVersions } from "app/services/SwHook";
import { getHooksVersion } from "app/UtilitiesAndConstants/UtilityFunctions/hooksUtils";
import { isSI } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";
import LocationService from "pro-features/components/Modals/src/UnlockModal/LocationService";
import type {
    IUnlockModalConfigTypes,
    UnlockModalConfigType,
} from "pro-features/components/Modals/src/UnlockModal/unlockModalConfig";

const HOOKS_V2_MODAL_ID = "FeaturePresentation";
export const DEFAULT_HOOKS_MODAL_ID = "Default";
const DEFAULT_SI_REDIRECT_HOOKS_MODAL_ID = "DefaultSIRedirect";

interface IFeaturePresentationHookInfo {
    modal: typeof HOOKS_V2_MODAL_ID;
    key: string;
    overrideTrackingKey?: string;
    location?: string;
    version?: string;
}

interface IHookInfo {
    modal: UnlockModalConfigType;
    key?: IUnlockModalConfigTypes[UnlockModalConfigType];
    overrideTrackingKey?: string;
    location?: string;
    version?: string;
}

export interface IWsConfig {
    wsComponent?: string;
    unlockHook: IHookInfo | IFeaturePresentationHookInfo;
}

export const getWsConfigFromState = (state, params?): IWsConfig => {
    const location = LocationService.getLocationTracking(state, params);

    const getHooksObjByVersion = (
        overrideTrackingKey?: string,
        key?: string,
        version?: string,
    ): IHookInfo | IFeaturePresentationHookInfo => {
        const isSIUser = isSI(swSettings);
        const hookId = isSIUser ? DEFAULT_SI_REDIRECT_HOOKS_MODAL_ID : DEFAULT_HOOKS_MODAL_ID;
        const keySuffix = isSIUser ? "SIRedirect" : "";
        return {
            modal: HOOKS_V2_MODAL_ID,
            key: key ? `${key}${keySuffix}` : hookId,
            overrideTrackingKey,
            location,
            version: version ?? EHooksVersions.V5,
        };
    };

    switch (state.name) {
        /* -- hooks 2.0 start -- */
        case "digitalsuite_website_audienceInterests":
            return { unlockHook: getHooksObjByVersion("WebAudienceInterestsFeature") };
        case "digitalsuite_marketresearch_webmarketanalysis_loyalty":
            return {
                wsComponent: "IndustryAnalysisAudienceLoyalty",
                unlockHook: getHooksObjByVersion("CategoryAudienceLoyalty"),
            };
        case "digitalsuite_app_appinterests":
            return {
                wsComponent: "WsAppInterests",
                unlockHook: {
                    modal: HOOKS_V2_MODAL_ID,
                    key: "AppInterestsFeature", // V2
                    location,
                },
            };
        case "accountreview_website_website_structure_folders":
        case "digitalsuite_website_folders":
            return {
                wsComponent: "FoldersAnalysis",
                unlockHook: getHooksObjByVersion(null, "WebContentFoldersFeature", EHooksVersions.V5),
            };
        case "accountreview_website_technologies":
        case "accountreview_website_technologies-dev":
            return {
                unlockHook: getHooksObjByVersion("WebsiteTechnologies"),
            };
        case "digitalsuite_websites_technologiesOverview":
            return {
                unlockHook: getHooksObjByVersion("WebsiteTechnologies", "websiteTechnologies", EHooksVersions.V5),
            };
        case "digitalsuite_website_popular_v2":
            return {
                unlockHook: getHooksObjByVersion("PopularPagesV2", "PopularPagesV2", EHooksVersions.V5),
            };
        case "digitalsuite_website_aitraffic":
            return {
                wsComponent: "AIRefferrals",
                unlockHook: getHooksObjByVersion("ChatbotAITraffic", "chatbotAITraffic", getHooksVersion(swSettings)),
            };
        /* -- hooks 2.0 end -- */
        // Web Analysis
        case "accountreview_website_overview_websiteperformance":
        case "accountreview_website_overview_websiteperformance-dev":
        case "digitalsuite_website_websiteperformance":
            return {
                wsComponent: "WsWebOverview",
                unlockHook: getHooksObjByVersion("WebsitePerformance"),
            };
        case "digitalsuite_website_content":
            return {
                wsComponent: "WsWebWebsiteContent",
                unlockHook: getHooksObjByVersion("WebsiteContent", "WebsiteContent", EHooksVersions.V5),
            };

        case "accountreview_website_competitivelandscape":
        case "digitalsuite_website_competitivelandscape":
            return {
                unlockHook: getHooksObjByVersion("CompetitiveLandscape"),
            };
        case "accountreview_website_trafficandengagement":
        case "digitalsuite_website_trafficandengagement":
            return {
                wsComponent: "WsWebTrafficAndEngagement",
                unlockHook: getHooksObjByVersion(
                    "TrafficAndEngagement",
                    "trafficAndEngagement",
                    getHooksVersion(swSettings),
                ),
            };
        case "accountreview_website_marketingchannels":
        case WEBSITE_ANALYSIS_MARKETING_CHANNELS:
        case "digitalsuite_website_marketingchannels":
            return {
                wsComponent: "WsWebTrafficChannels",
                unlockHook: getHooksObjByVersion("MarketingChannels", "marketingChannels", getHooksVersion(swSettings)),
            };
        case "organicsearch_websites_v2_traffic_keywords":
        case "accountreview_website_search_keyword":
        case WEBSITE_ANALYSIS_MARKETING_KEYWORDS:
            return {
                wsComponent: "WsWebSearchKeywords",
                unlockHook: getHooksObjByVersion("Keywords", "keywords", EHooksVersions.V5),
            };
        case "organicsearch_website_search_phrases":
            return {
                wsComponent: "WsWebSearchPhrases",
                unlockHook: getHooksObjByVersion("SearchPhrases", "searchPhrases"),
            };
        case "accountreview_website_search_overview":
        case WEBSITE_ANALYSIS_MARKETING_SEARCH_OVERVIEW:
            return {
                wsComponent: "WsWebSearchOverview",
                unlockHook: getHooksObjByVersion("SearchOverview", "websiteSearchOverview", EHooksVersions.V6),
            };
        case "organicsearch_website_organic_search_overview":
        case "digitalsuite_website_search_overview":
            return {
                wsComponent: "WsWebSearchOverview",
                unlockHook: getHooksObjByVersion("SearchOverview", "paidSearchOverview", EHooksVersions.V6),
            };
        case "paidsearch_website_ads":
        case "accountreview_website_search_ads":
        case WEBSITE_ANALYSIS_MARKETING_SEARCH_ADS:
            return {
                wsComponent: "WsWebSearchAds",
                unlockHook: getHooksObjByVersion("SearchPLAs", "searchPLAs"),
            };
        case "organicsearch_websites-competitorsKeywords":
            return {
                wsComponent: "WsWebOrganicSearchCompetitors",
                unlockHook: getHooksObjByVersion("KwCompetitors"),
            };
        case "accountreview_website_search_organic_competitors":
        case WEBSITE_ANALYSIS_MARKETING_SEARCH_COMPETITORS:
            return {
                wsComponent: "WsWebOrganicSearchCompetitors",
                unlockHook: getHooksObjByVersion("KwCompetitors", "organicKwCompetitors"),
            };

        case "paidsearch_website_search_paid_competitors":
            return {
                wsComponent: "WsWebPaidSearchCompetitors",
                unlockHook: getHooksObjByVersion("KwCompetitors", "paidKwCompetitors"),
            };
        case "websites-trafficDisplay":
            return {
                wsComponent: "WsWebDisplayTrafficOverview",
                unlockHook: getHooksObjByVersion("Overview"),
            };
        case "accountreview_website_display_overview":
        case WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW:
            return {
                wsComponent: "WsWebDisplayTrafficOverview",
                unlockHook: getHooksObjByVersion("Overview"),
            };
        case "accountreview_website_display_ad_networks":
        case WEBSITE_ANALYSIS_MARKETING_DISPLAY_AD_NETWORKS:
            return {
                wsComponent: "WsWebDisplayTrafficAdNetworks",
                unlockHook: getHooksObjByVersion("UserAcquisition"),
            };
        case "digitalsuite_website_display_publishers":
            return {
                wsComponent: "WsWebDisplayTrafficPublishers",
                unlockHook: getHooksObjByVersion("Publishers", "publishers", getHooksVersion(swSettings)),
            };
        case "digitalsuite_website_display_overview":
            return {
                wsComponent: "WsWebDisplayTrafficOverview",
                unlockHook: getHooksObjByVersion("Overview", "trafficOverview", getHooksVersion(swSettings)),
            };
        case "digitalsuite_website_display_videos":
            return {
                wsComponent: "WsWebDisplayTrafficVideos",
                unlockHook: getHooksObjByVersion("Videos", "videos", getHooksVersion(swSettings)),
            };
        case "digitalsuite_website_display_ad_networks":
            return {
                wsComponent: "WsWebDisplayTrafficAdNetworks",
                unlockHook: getHooksObjByVersion("UserAcquisition", "userAcquisition", getHooksVersion(swSettings)),
            };
        // SocialOverviewPage
        case "accountreview_website_social_overview":
        case WEBSITE_ANALYSIS_MARKETING_SOCIAL_OVERVIEW:
            return {
                wsComponent: "WsWebSocialTrafficOverview",
                unlockHook: getHooksObjByVersion("SocialOverview"),
            };
        case "digitalsuite_website_social_overview":
            return {
                wsComponent: "WsWebSocialTrafficOverview",
                unlockHook: getHooksObjByVersion("SocialOverview", "socialOverview", getHooksVersion(swSettings)),
            };
        case "accountreview_website_subdomains":
        case "digitalsuite_website_subdomains":
            return {
                wsComponent: "WsWebContentSubdomains",
                unlockHook: getHooksObjByVersion("Subdomains", "subdomains", EHooksVersions.V5),
            };
        case "accountreview_website_website_structure_pages":
        case "digitalsuite_website_popular":
            return {
                wsComponent: "PopularPagesV2",
                unlockHook: getHooksObjByVersion("Pages"),
            };
        // AdvertisersContainer
        case "digitalsuite_websites_paidoutgoing_advertisers":
            return {
                wsComponent: "WsWebMonetizationAdvertisers",
                unlockHook: getHooksObjByVersion("Advertisers", "advertisers", getHooksVersion(swSettings)),
            };
        // MonetizationNetworksContainer
        case "digitalsuite_websites_paidoutgoing_monetization_networks":
            return {
                wsComponent: "WsWebMonetizationAdvertisers",
                unlockHook: getHooksObjByVersion("AdNetworks", "adNetworks", getHooksVersion(swSettings)),
            };
        // Web Categories Analysis
        case "industryAnalysis-root-homeDuplicate":
        case "digitalsuite_marketresearch_webmarketanalysis_overview":
        case "digitalsuite_marketresearch_webmarketanalysis_overview_duplicate":
            return {
                wsComponent: "WsWebCategoryPerformance",
                unlockHook: getHooksObjByVersion("CategoryPerformance"),
            };
        case "digitalsuite_marketresearch_webmarketanalysis_trends":
            return {
                wsComponent: "WsWebCategoryShare",
                unlockHook: getHooksObjByVersion("CategoryShare"),
            };
        case "market_research_demand_trends":
        case "market_research_demand_trends_v2":
            return {
                wsComponent: "IndustrySearchInterests",
                unlockHook: getHooksObjByVersion("IndustrySearchInterests"),
            };
        case "digitalsuite_marketresearch_webmarketanalysis_trafficChannels":
        case "market_research_traffic_channels_v2":
            return {
                wsComponent: "IndustryAnalysisMarketingChannels",
                unlockHook: getHooksObjByVersion("IndustryAnalysisMarketingChannels"),
            };
        case "market_research_overview_v2":
            return {
                wsComponent: MARKET_RESEARCH_PERFORMANCE_CONFIG_ID,
                unlockHook: getHooksObjByVersion(MARKET_RESEARCH_PERFORMANCE_CONFIG_ID),
            };
        case "market_research_home":
            return {
                wsComponent: MARKET_RESEARCH_HOME_CONFIG_ID,
                unlockHook: getHooksObjByVersion(MARKET_RESEARCH_HOME_CONFIG_ID),
            };
        case "market_research_players":
        case "market_research_players_v2":
            return {
                wsComponent: MARKET_PLAYERS_CONFIG_ID,
                unlockHook: getHooksObjByVersion(MARKET_PLAYERS_CONFIG_ID),
            };
        case "market_research_segmentation":
        case "market_research_segmentation_v2":
            return {
                wsComponent: INDUSTRY_SEGMENTATION_CONFIG_ID,
                unlockHook: getHooksObjByVersion(INDUSTRY_SEGMENTATION_CONFIG_ID),
            };
        case "digitalsuite_marketresearch_webmarketanalysis_geography":
        case "market_research_geography":
        case "market_research_geography_v2":
            return {
                wsComponent: "IndustryAnalysisGeography",
                unlockHook: getHooksObjByVersion("IndustryAnalysisGeography"),
            };
        case "digitalsuite_marketresearch_webmarketanalysis_demographics":
        case "market_research_demography":
        case "market_research_demography_v2":
            return {
                wsComponent: "WsCategoryDemographics",
                unlockHook: getHooksObjByVersion("Demographics"),
            };
        case "digitalsuite_marketresearch_webmarketanalysis_mapping":
            return {
                wsComponent: "WsWebCategoryTopSites",
                unlockHook: getHooksObjByVersion("TopWebsites"),
            };
        case "digitalsuite_marketresearch_webmarketanalysis_rankings_search":
            return {
                wsComponent: "WsWebCategorySearchLeaders",
                unlockHook: getHooksObjByVersion("SearchLeaders"),
            };
        case "digitalsuite_marketresearch_webmarketanalysis_rankings_social":
            return {
                wsComponent: "WsWebCategorySocialLeaders",
                unlockHook: getHooksObjByVersion("SocialLeaders"),
            };
        case "industryAnalysis-categoryLeadersSearchAds":
        case "digitalsuite_marketresearch_webmarketanalysis_rankings_ads":
            return {
                wsComponent: "WsWebCategoryDisplayLeaders",
                unlockHook: getHooksObjByVersion("DisplayLeaders"),
            };
        case "industryAnalysis-categoryLeadersSearchReferrals":
        case "digitalsuite_marketresearch_webmarketanalysis_rankings_referrals":
            return {
                wsComponent: "WsWebCategoryReferralLeaders",
                unlockHook: getHooksObjByVersion("ReferralLeaders"),
            };
        case "digitalsuite_marketresearch_webmarketanalysis_rankings_direct":
            return {
                wsComponent: "WsWebCategoryDirectLeaders",
                unlockHook: getHooksObjByVersion("DirectLeaders"),
            };
        case "industryAnalysis-categoryLeadersSearchMail":
            return {
                wsComponent: "WsWebCategoryEmailLeaders",
                unlockHook: getHooksObjByVersion("EmailLeaders"),
            };
        case "industryAnalysis-topKeywords":
            return {
                wsComponent: "WsTopKeywords",
                unlockHook: getHooksObjByVersion("TopKeywords"),
            };
        // Web Keywords Analysis
        case "digitalsuite_findkeywords_keywordGeneratorTool":
            return {
                wsComponent: "KeywordsGenerator",
                unlockHook: getHooksObjByVersion("KeywordsGenerator"),
            };
        // App Analysis
        case "salesIntelligence-apps-performance":
            return {
                wsComponent: "WsAppPerformance",
                unlockHook: getHooksObjByVersion("AppPerformance"),
            };
        case "salesIntelligence-apps-rankingdaily":
        case "salesIntelligence-apps-rankinghistory":
        case "digitalsuite_app_apprankinghistory":
        case "digitalsuite_app_apprankingdaily":
            return {
                wsComponent: "WsAppRanking",
                unlockHook: getHooksObjByVersion("Ranking"),
            };
        case "digitalsuite_app_storeengagement":
        case "salesIntelligence-apps-storeengagement":
        case "salesIntelligence-apps-usersengagement":
        case "digitalsuite_app_usersengagement":
            return {
                wsComponent: "WsAppEngagement",
                unlockHook: getHooksObjByVersion("Engagement"),
            };
        case "digitalsuite_app_appdmg":
        case "salesIntelligence-apps-demographics":
            return {
                wsComponent: "WsAppDemographics",
                unlockHook: getHooksObjByVersion("Demographics"),
            };
        case "salesIntelligence-apps-appaudienceinterests":
            return {
                wsComponent: "WsAppInterests",
                unlockHook: getHooksObjByVersion("Interests"),
            };
        // App Categories Analysis
        case APP_CATEGORY_LEADERBOARD:
            return {
                wsComponent: "WsTopApps",
                unlockHook: getHooksObjByVersion("TopApps"),
            };
        case APP_CATEGORY_TRENDS:
        case "digitalsuite_marketresearch_appmarketanalysis_trending":
            return {
                wsComponent: "WsTrendingApps",
                unlockHook: getHooksObjByVersion("TrendingApps"),
            };
        // Dashboards
        case "dashboard":
        case "dashboard-new":
        case "dashboard-gallery":
            return {
                wsComponent: null,
                unlockHook: getHooksObjByVersion("CustomDashboards"),
            };
        // Lead Generator
        case "leadGenerator":
            return {
                wsComponent: null,
                unlockHook: getHooksObjByVersion("LeadGenerator"),
            };
        // Report Generator
        case "cig":
            return {
                wsComponent: null,
                unlockHook: getHooksObjByVersion("CIG"),
            };
        // Conversion module
        case "conversion":
            return {
                wsComponent: "Conversion",
                unlockHook: getHooksObjByVersion("ConversionModule"),
            };
        case "conversion.home":
            return {
                wsComponent: "Conversion",
                unlockHook: getHooksObjByVersion("ConversionModule"),
            };
        case "conversion-customgroup":
            return {
                wsComponent: "Conversion",
                unlockHook: getHooksObjByVersion("ConversionCategoryOverview"),
            };
        case "conversion.websiteoverview":
            return {
                wsComponent: "Conversion",
                unlockHook: getHooksObjByVersion("ConversionWebsiteOverview"),
            };
        case "salesIntelligence-apps-home":
            return {
                wsComponent: "AppAnalysisHome",
                unlockHook: getHooksObjByVersion("AppPerformance"),
            };
        case "digitalsuite_segments-homepage":
            return {
                unlockHook: {
                    modal: HOOKS_V2_MODAL_ID,
                    key: "CustomSegments", // V2
                    location,
                },
            };
        case "digitalsuite_marketresearch_keywordmarketanalysis_home":
            return {
                unlockHook: {
                    modal: HOOKS_V2_MODAL_ID,
                    key: "KeywordAnalysis", // V2
                    location,
                },
            };
        case "brandmonitoring.home":
        case "brandmonitoring.competitors":
        case "brandmonitoring.searchads":
            return {
                wsComponent: "BrandMonitor",
                unlockHook: getHooksObjByVersion(null, "brandMonitoring", EHooksVersions.V5),
            };
        case "digitalsuite_marketresearch_webmarketanalysis_rankings":
            return {
                wsComponent: "CategoryLeaders",
                unlockHook: getHooksObjByVersion(
                    "AcquisitionLeaders",
                    "acquisitionLeaders",
                    getHooksVersion(swSettings),
                ),
            };
        case DIGITALSUITE_WEBSITE_PUBLISHER_OVERVIEW:
            return {
                wsComponent: "WsWebDisplayTrafficOverview",
                unlockHook: getHooksObjByVersion(null, "publisherOverview", getHooksVersion(swSettings)),
            };
        case DIGITALSUITE_WEBSITE_AUDIENCE:
            return {
                wsComponent: "WebAudience",
                unlockHook: getHooksObjByVersion(undefined, "audienceOverview", EHooksVersions.V5),
            };
        case DIGITALSUITE_WEBSITE_REFERRALS:
            return {
                wsComponent: "WebReferralTraffic",
                unlockHook: getHooksObjByVersion(undefined, "referrals", EHooksVersions.V5),
            };
        default:
            return {
                wsComponent: null,
                unlockHook: {
                    modal: HOOKS_V2_MODAL_ID, // Contact US Modal
                    key: DEFAULT_HOOKS_MODAL_ID,
                    location,
                },
            };
    }
};
